/**
 * Platform app switcher ("waffle" / grid menu) — REQUIRED in every platform frontend.
 *
 * Placement is mandatory and enforced by AppHeader: this is the **very first element in the
 * header**, left of the logo, left of the mobile hamburger, left of everything. Users must find
 * it in the same spot in every app (verification item 22).
 *
 * The popover layout, sizes and colours below are the platform spec verbatim (PLAN.md §2.5 /
 * CLAUDE.md "Popover panel (visual design — MANDATORY)").
 */

import { Popover, Transition } from '@headlessui/react';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { Fragment, type ComponentType, type SVGProps } from 'react';

import { useAppDirectory } from '@/hooks/useAppDirectory';
import { resolveAppIcon } from '@/utils/appIcons';
import type { AppDirectoryEntry } from '@/types';

interface GridMenuProps {
  /** This app's slug, so its own tile renders in the active state at rest (item 24). */
  currentSlug: string;
}

export function GridMenu({ currentSlug }: GridMenuProps) {
  const { data: apps = [], isLoading } = useAppDirectory();

  return (
    <Popover className="relative">
      <Popover.Button
        aria-label="Platform apps"
        className="p-2 rounded-lg text-surface-500 hover:text-surface-700 hover:bg-surface-100 dark:text-surface-400 dark:hover:text-surface-200 dark:hover:bg-surface-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
      >
        <Squares2X2Icon className="h-5 w-5" aria-hidden="true" />
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-300"
        enterFrom="opacity-0 -translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-2"
      >
        <Popover.Panel className="absolute left-0 top-full mt-2 z-50 w-72 p-3 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 shadow-modal">
          <div className="px-1 pb-2 border-b border-surface-200 dark:border-surface-700 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-surface-500 dark:text-surface-400">
              Platform apps
            </span>
          </div>

          {isLoading ? (
            <p className="py-6 text-center text-sm text-surface-500 dark:text-surface-400">
              Loading…
            </p>
          ) : apps.length === 0 ? (
            <p className="py-6 text-center text-sm text-surface-500 dark:text-surface-400">
              No apps available
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {apps.map((app) => (
                <AppTile key={app.slug} app={app} isCurrent={app.slug === currentSlug} />
              ))}
            </div>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}

function AppTile({ app, isCurrent }: { app: AppDirectoryEntry; isCurrent: boolean }) {
  const Icon: ComponentType<SVGProps<SVGSVGElement>> = resolveAppIcon(app.icon_name);

  // The current app's tile renders in the hover/active visual state at rest, so users can see
  // where they are (item 24). `display_color` tints it when the app supplies one.
  const activeStyle =
    isCurrent && app.display_color
      ? { backgroundColor: `${app.display_color}1a`, color: app.display_color }
      : undefined;

  const handleClick = () => {
    if (!app.url) return;
    // Full-page navigation, not router.push — sibling apps live on different origins.
    window.location.href = app.url;
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={app.description ?? app.name}
      aria-current={isCurrent ? 'page' : undefined}
      style={activeStyle}
      className={[
        'group flex flex-col items-center justify-center text-center p-3 min-h-[5.5rem] rounded-lg transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        isCurrent && !app.display_color
          ? 'bg-primary-50 dark:bg-primary-500/10'
          : 'hover:bg-primary-50 dark:hover:bg-primary-500/10',
      ].join(' ')}
    >
      {app.icon_name || !app.icon_url ? (
        <Icon
          className={[
            'h-6 w-6 transition-colors',
            isCurrent
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-primary-400 group-hover:text-primary-500 dark:group-hover:text-primary-400',
          ].join(' ')}
          style={isCurrent && app.display_color ? { color: app.display_color } : undefined}
          aria-hidden="true"
        />
      ) : (
        <img src={app.icon_url} className="h-6 w-6" alt="" />
      )}
      <span
        className={[
          'mt-2 text-xs leading-tight line-clamp-2 transition-colors',
          isCurrent
            ? 'font-semibold text-primary-700 dark:text-primary-300'
            : 'font-medium text-surface-600 dark:text-surface-400 group-hover:font-semibold group-hover:text-primary-700 dark:group-hover:text-primary-300',
        ].join(' ')}
      >
        {app.name}
      </span>
    </button>
  );
}
